import { warnPlaceholderOnce } from './placeholder-warnings';

/**
 * The key a host puts its values under in `componentProps`, for any component
 * whose text fills `{{ … }}` placeholders:
 *
 * ```tsx
 * componentProps={{ [textColumnId]: { placeholderValues: { user } } }}
 * ```
 */
export const PLACEHOLDER_VALUES_PROP = 'placeholderValues';

/** The object or array that placeholder paths are read from. */
export type PlaceholderValues = Record<string, unknown> | unknown[];

const isPlaceholderValues = (value: unknown): value is PlaceholderValues =>
  typeof value === 'object' && value !== null;

/**
 * The host's `placeholderValues` as something paths can be read from, or
 * `undefined` when there is nothing to read.
 *
 * Takes an object or array as it is, and a JSON string holding one, for hosts
 * that already have their values as JSON. Anything else — a string that is not
 * JSON, JSON for a bare number or string, a function — counts as no values, so
 * every placeholder prints its fallback or nothing. The value crosses a package
 * boundary untyped, so none of that may throw.
 */
export function resolvePlaceholderValues(value: unknown): PlaceholderValues | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value);
      if (isPlaceholderValues(parsed)) {
        return parsed;
      }
    } catch {
      // Not JSON at all; warned about below along with JSON that is not an object.
    }
    warnPlaceholderOnce(
      `RuleCMS: ${PLACEHOLDER_VALUES_PROP} is a string, but not JSON for an object or array, ` +
        `so {{ … }} placeholders show their fallbacks or nothing. It starts ` +
        `${JSON.stringify(value.slice(0, 40))}.`
    );
    return undefined;
  }

  if (isPlaceholderValues(value)) {
    return value;
  }

  warnPlaceholderOnce(
    `RuleCMS: ${PLACEHOLDER_VALUES_PROP} must be an object, an array, or a JSON string holding ` +
      `one, but it is a ${typeof value}, so {{ … }} placeholders show their fallbacks or nothing.`
  );
  return undefined;
}
